import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Paper
} from '@mui/material';
import { searchProcedureCodesByType } from '../../services/procedureCodesService';

// Simple debounce utility (no lodash needed)
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  
  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
  
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return debouncedCallback;
};

const SimpleProcedureCodeAutocomplete = ({
  codeType, // 'LOINC', 'SNOMED', 'ICD-10', 'NDC', 'CVX', 'Custom'
  value = null,
  onChange,
  placeholder = "Search procedure codes...",
  label = "Procedure Code",
  maxResults = 20,
  disabled = false,
  required = false,
  error = false,
  helperText = "",
  searchDelay = 300,
  minSearchLength = 2,
  ...props
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Search function
  const searchCodes = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < minSearchLength) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const results = await searchProcedureCodesByType(
        codeType, 
        searchTerm, 
        true // activeOnly
      );
      
      // Limit results for performance
      const limitedResults = results.slice(0, maxResults);
      setOptions(limitedResults);
    } catch (error) {
      console.error('Error searching procedure codes:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  const debouncedSearch = useDebounce(searchCodes, searchDelay);

  // Trigger search when input changes
  useEffect(() => {
    debouncedSearch(inputValue);
  }, [inputValue, debouncedSearch]);

  return (
    <Autocomplete
      {...props}
      value={value}
      onChange={(event, newValue) => {
        onChange?.(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      loading={loading}
      disabled={disabled}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return `${option.procedure_code} - ${option.name}`;
      }}
      isOptionEqualToValue={(option, value) => {
        return option?.id === value?.id;
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label={codeType} 
                size="small" 
                variant="outlined"
                sx={{ 
                  fontSize: '0.75rem', 
                  height: 20,
                  backgroundColor: 'primary.light',
                  color: 'primary.dark'
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {option.procedure_code}
              </Typography>
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary', 
                fontSize: '0.875rem',
                mt: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {option.name}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      PaperComponent={({ children, ...props }) => (
        <Paper {...props} sx={{ maxHeight: 300 }}>
          {children}
        </Paper>
      )}
      noOptionsText={
        inputValue.length < minSearchLength 
          ? `Type ${minSearchLength}+ characters to search...` 
          : "No matching codes found"
      }
      loadingText="Searching codes..."
    />
  );
};

export default SimpleProcedureCodeAutocomplete; 